import React from 'react'
import cn from 'classnames'
import { Film, Music2, Pause, Play } from 'lucide-react'
import styles from './media-dock.module.scss'

// A song's chance as a label: whole percents where they are readable, one
// decimal below 10%, and never a misleading "0%" for a live long shot.
export function formatRadioChance(chance) {
    const percent = chance * 100
    if (percent >= 10) return `${Math.round(percent)}%`
    if (percent >= 0.1) return `${Number(percent.toFixed(1))}%`
    return '<0.1%'
}

const Equalizer = () => (
    <span className={styles.equalizer} aria-hidden="true">
        <span />
        <span />
        <span />
    </span>
)

/**
 * One song in the radio draw: cover art, title, why it is in the draw, and
 * its chance to play next, with the chance drawn along the bottom edge. The
 * dock's Radio tab and the station thread's "Up next" reply both render it,
 * so the draw reads the same everywhere.
 *
 * `thumbAction` turns the cover into a play/pause button (the thread plays a
 * contender through the dock); without it the row is display-only, as in the
 * dock, where the broadcast cannot be picked from. `onOpen` makes the row a
 * link to the song.
 *
 * @param {{
 *   title: string,
 *   artworkUrl?: string | null,
 *   isVideo?: boolean,
 *   chance?: number | null,
 *   chanceSupport?: number,
 *   chanceDiscovery?: number,
 *   playlistCount?: number,
 *   upvalueSats?: number,
 *   maxChance?: number,
 *   formatSats: (sats: number) => string,
 *   isActive?: boolean,
 *   activeLabel?: string | null,
 *   thumbAction?: { playing: boolean, onToggle: (event: any) => void, disabled?: boolean } | null,
 *   onOpen?: ((event: any) => void) | null,
 *   testId?: string,
 * }} props
 */
export default function RadioChanceRow({
    title,
    artworkUrl = null,
    isVideo = false,
    chance = null,
    chanceSupport = 0,
    chanceDiscovery = 0,
    playlistCount = 0,
    upvalueSats = 0,
    maxChance = 0,
    formatSats,
    isActive = false,
    activeLabel = null,
    thumbAction = null,
    onOpen = null,
    testId = undefined,
}) {
    const hasOdds = chance != null
    const isResting = hasOdds && chance <= 0
    const isDiscovery = chanceDiscovery > 0
    const coverStyle = artworkUrl ? { backgroundImage: `url(${artworkUrl})` } : undefined
    const placeholder =
        !artworkUrl &&
        (isVideo ? <Film className={styles.thumbIcon} /> : <Music2 className={styles.thumbIcon} />)

    const thumb = thumbAction ? (
        <button
            type="button"
            className={cn(
                styles.thumb,
                artworkUrl && styles.thumbWithPoster,
                styles.radioThumbButton,
                thumbAction.playing && styles.radioThumbPlaying,
            )}
            style={coverStyle}
            onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                thumbAction.onToggle(event)
            }}
            disabled={thumbAction.disabled}
            aria-label={thumbAction.playing ? `Pause ${title}` : `Play ${title}`}
            aria-pressed={thumbAction.playing}
        >
            <span className={styles.radioThumbScrim} aria-hidden="true">
                {thumbAction.playing ? <Pause /> : <Play />}
            </span>
        </button>
    ) : (
        <span
            className={cn(styles.thumb, artworkUrl && styles.thumbWithPoster)}
            style={coverStyle}
            aria-hidden="true"
        >
            {placeholder}
        </span>
    )

    const open = onOpen
        ? {
              role: 'button',
              tabIndex: 0,
              onClick: onOpen,
              onKeyDown: event => {
                  // Keys on the cover's play button are that button's own.
                  if (event.target !== event.currentTarget) return
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  onOpen(event)
              },
          }
        : {}

    return (
        <div
            className={cn(
                styles.historyItem,
                !onOpen && styles.radioItemStatic,
                isActive && styles.historyItemActive,
                isResting && !isActive && styles.radioItemResting,
            )}
            data-testid={testId}
            {...open}
        >
            {thumb}
            <span className={styles.historyItemMeta}>
                <span className={styles.historyItemTitle} title={title}>
                    {title}
                </span>
                <span className={styles.historyItemStatus}>
                    {isActive && activeLabel ? (
                        <>
                            <Equalizer /> {activeLabel}
                        </>
                    ) : isResting ? (
                        'Resting this draw — aired recently'
                    ) : !hasOdds ? (
                        'In rotation'
                    ) : (
                        <>
                            {chanceSupport > 0 && (
                                <span
                                    title={`Backed: ${playlistCount} playlists × √${upvalueSats} sats`}
                                >
                                    {playlistCount} {playlistCount === 1 ? 'playlist' : 'playlists'}{' '}
                                    · {formatSats(upvalueSats)}
                                </span>
                            )}
                            {isDiscovery && (
                                <span
                                    className={styles.radioDiscoveryTag}
                                    title="Discovery: unheard this week, so it shares the discovery slice equally"
                                >
                                    Discovery · unheard this week
                                </span>
                            )}
                        </>
                    )}
                </span>
            </span>
            {hasOdds && !isResting && (
                <span
                    className={cn(
                        styles.radioChance,
                        isDiscovery && chanceSupport <= 0 && styles.radioChanceDiscovery,
                    )}
                    title="Chance to play next"
                >
                    {formatRadioChance(chance)}
                </span>
            )}
            {hasOdds && maxChance > 0 && chance > 0 && (
                <span
                    className={cn(
                        styles.radioChanceBar,
                        chanceSupport <= 0 && styles.radioChanceBarDiscovery,
                    )}
                    style={{ width: `${(chance / maxChance) * 100}%` }}
                    aria-hidden="true"
                />
            )}
        </div>
    )
}
